// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldInputProps } from 'react-final-form';
import { Label } from 'components/atoms/Label';
import { ErrorText } from 'components/atoms/ErrorText';
import useOutsideClick from 'util/hooks/useClickOutside';
import styles from './SelectField.module.scss';
import { IconDropDown } from 'components/atoms/icons/IconDropDown';
import { FieldValidator } from 'final-form';
import Portal from 'components/atoms/Portal';
import { useGetBoundingClientRect } from 'util/hooks/useGetBoundingClientRect';
import { mergeClasses } from 'util/commons';
import { IconClose } from 'components/atoms/icons/IconClose';
import { useTranslation } from 'react-i18next';

export interface SelectOption {
  label: string;
  value: string | number;
  type?: string;
}

interface Props {
  className?: string;
  containerClassName?: string;
  wrapperClassName?: string;
  name: string;
  options: SelectOption[];
  label?: string | null;
  value?: string;
  required?: boolean;
  placeholder?: string | null;
  disabled?: boolean;
  onChange?: (value: string | number) => void;
  validate?: FieldValidator<string>;
  isDarkmode?: boolean;
  isLanguage?: boolean;
  showPlaceholderOption?: boolean;
  showValue?: boolean;
  usePortal?: boolean;
  allowClear?: boolean;
  menuPlacement?: 'top' | 'bottom' | 'auto';
  dropdownMaxHeight?: number;
  searchable?: boolean;
  fontSize?: 'medium' | 'large';
}

export function SelectField({
  label,
  name,
  value,
  placeholder,
  options,
  disabled,
  className,
  containerClassName,
  wrapperClassName,
  onChange,
  required,
  validate,
  isDarkmode,
  isLanguage,
  showPlaceholderOption,
  showValue,
  usePortal,
  allowClear,
  menuPlacement = 'auto',
  dropdownMaxHeight = 210,
  searchable,
  fontSize,
}: Props) {
  const [inputValue, setInputValue] = useState<{
    label: string;
    value: string | number;
  }>();
  const [isOpenOptions, setIsOpenOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionListRef = useRef<HTMLUListElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    value
      ? setInputValue(
          options.find((option) => String(option.value) === value) || undefined
        )
      : setInputValue(undefined);
  }, [value]);

  // Close dropdown when scrolling or resizing to prevent position issues
  useEffect(() => {
    if (!usePortal || !isOpenOptions) return;

    const handleClose = (event: Event) => {
      // Don't close if scrolling within the dropdown itself
      if (
        optionListRef.current &&
        event.target &&
        optionListRef.current.contains(event.target as Node)
      ) {
        return;
      }
      setIsOpenOptions(false);
    };

    const handleResize = () => {
      setIsOpenOptions(false);
    };

    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [usePortal, isOpenOptions]);

  // Close dropdown when scrolling or resizing to prevent position issues
  useEffect(() => {
    if (!usePortal || !isOpenOptions) return;

    const handleClose = (event: Event) => {
      // Don't close if scrolling within the dropdown itself
      if (
        optionListRef.current &&
        event.target &&
        optionListRef.current.contains(event.target as Node)
      ) {
        return;
      }
      setIsOpenOptions(false);
    };

    const handleResize = () => {
      setIsOpenOptions(false);
    };

    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [usePortal, isOpenOptions]);

  const { ref: dropDownRef } = useOutsideClick({
    onClickOutside: () => {
      setIsOpenOptions(false);
    },
    exceptRefs: [containerRef],
  });

  const { domRect } = useGetBoundingClientRect(
    {
      el: containerRef.current,
    },
    [isOpenOptions]
  );

  const dropdownPlacement = useMemo(() => {
    if (!domRect || !usePortal) return menuPlacement;

    if (menuPlacement !== 'auto') return menuPlacement;

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - domRect.bottom;
    const spaceAbove = domRect.top;

    const requiredHeight = dropdownMaxHeight + 10;

    if (spaceBelow < requiredHeight && spaceAbove >= requiredHeight) {
      return 'top';
    }

    return 'bottom';
  }, [domRect, usePortal, menuPlacement, dropdownMaxHeight]);

  useEffect(() => {
    setFilterOptions(options);
  }, [isOpenOptions, options]);

  const handleOnSearch = (keyword: string) => {
    if (!keyword) {
      setFilterOptions(options);
      return;
    }

    const searchOptions = options.filter((item) => {
      if (item.label.toLowerCase().includes(keyword.toLowerCase())) {
        return item;
      }
    });

    setFilterOptions(searchOptions);
  };

  const renderOptions = (input: FieldInputProps<string, HTMLElement>) => (
    <ul
      ref={optionListRef}
      className={mergeClasses(styles.SelectOptionsList, {
        [styles.DarkSelectOptionsList]: isDarkmode,
        [styles.SelectOptionsListPortal]: usePortal,
        [styles.SelectOptionsListLarge]: fontSize === 'large',
        [styles.menuPlacementTop]: !usePortal && dropdownPlacement === 'top',
      })}
      style={{
        maxHeight: dropdownMaxHeight,
        transform:
          usePortal && dropdownPlacement === 'top' ? 'translateY(-100%)' : '',
      }}
    >
      {!isLanguage &&
        placeholder &&
        !showPlaceholderOption &&
        options.length > 0 && (
          <li
            className={`${!inputValue?.value ? styles.selected : ''} `}
            onClick={() => {
              input.onChange('');
              setInputValue(undefined);
              onChange && onChange('');
            }}
          >
            {placeholder}
          </li>
        )}
      {searchable && options.length > 0 && (
        <li className={isDarkmode ? styles.darkModeItem : ''}>
          <div className={styles.searchInput}>
            <input
              placeholder={t('common.placeholder.search') || ''}
              onChange={(e) => handleOnSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </li>
      )}
      {filterOptions.length > 0 ? (
        filterOptions.map((option, i) => {
          return (
            <li
              className={mergeClasses({
                [styles.selected]: option.value === inputValue?.value,
              })}
              key={i}
              title={option.label}
              onClick={() => {
                input.onChange(String(option.value));
                setInputValue(option);
                onChange && onChange(String(option.value));
                setFilterOptions(options);
              }}
            >
              {option.label}
            </li>
          );
        })
      ) : (
        <li
          style={{
            textAlign: 'center',
          }}
        >
          {t('messages.M_008')}
        </li>
      )}
    </ul>
  );

  const renderOptionsList = (input: FieldInputProps<string, HTMLElement>) => {
    if (!isOpenOptions) {
      return null;
    }

    if (usePortal && domRect) {
      const topPosition =
        dropdownPlacement === 'top'
          ? domRect.top - 4 + ((labelRef.current?.offsetHeight || -8) + 8)
          : domRect.bottom + 4;

      return (
        <Portal>
          <div ref={dropDownRef}>
            <div
              style={{
                position: 'absolute',
                top: topPosition,
                left: domRect.left,
                width: domRect.width,
                height: 1,
                zIndex: 100000 - 1,
              }}
            >
              {renderOptions(input)}
            </div>
          </div>
        </Portal>
      );
    }

    return renderOptions(input);
  };

  return (
    <Field name={name} value={value} validate={validate}>
      {({ input, meta }) => {
        const displayedOption = options.find(
          (o) =>
            String(o.value) === String(input.value) ||
            String(o.value) === String(value)
        );

        if (displayedOption) {
          setInputValue(displayedOption);
        }

        return (
          <div
            ref={usePortal ? containerRef : dropDownRef}
            className={mergeClasses(
              className ?? styles.selectField,
              containerClassName,
              {
                [styles.selectFieldDarkmode]: isDarkmode,
              }
            )}
            onClick={() => {
              if (!disabled) {
                setIsOpenOptions(!isOpenOptions);
              }
            }}
          >
            {label && (
              <div ref={labelRef}>
                <Label text={label} tag={'div'} required={required} />
              </div>
            )}

            <div className={mergeClasses(styles.select, wrapperClassName)}>
              <input
                {...input}
                value={
                  (isLanguage || showValue
                    ? placeholder
                    : displayedOption?.label || placeholder) ?? undefined
                }
                className={mergeClasses(styles.input, {
                  [styles.empty]: !inputValue,
                  [styles.inputError]:
                    meta &&
                    (meta.touched || meta.modified) &&
                    meta.error &&
                    !isOpenOptions,
                  [styles.placeHolder]: !displayedOption,
                  [styles.inputDisable]: disabled,
                })}
                placeholder={placeholder || undefined}
                readOnly
                disabled={disabled}
              />
              {renderOptionsList(input)}

              {allowClear && Boolean(value) ? (
                <IconClose
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                  }}
                  width={10}
                  height={10}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange?.('');
                    input.onChange('');
                  }}
                />
              ) : (
                <IconDropDown />
              )}
            </div>

            {meta &&
              (meta.touched || meta.modified) &&
              meta.error &&
              !isOpenOptions && (
                <ErrorText text={meta.error} className={styles.error} />
              )}
          </div>
        );
      }}
    </Field>
  );
}
